import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import {
  getDefaultTokens,
  type ThemeTokens,
} from '../lib/theme-tokens';

const STORAGE_KEY = 'sd-theme-preference';

interface ThemeContextValue {
  theme: 'light' | 'dark';
  tokens: ThemeTokens;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: 'dark',
  tokens: getDefaultTokens('dark'),
  toggleTheme: () => {},
});

export function useTheme() {
  return useContext(ThemeContext);
}

function readStoredTheme(): 'light' | 'dark' | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch { /* localStorage unavailable */ }
  return null;
}

interface ThemeProviderProps {
  children: React.ReactNode;
  dataTheme?: 'light' | 'dark' | null | undefined;
  dataStyle?: string | null | undefined;
  shadowRoot?: ShadowRoot | null | undefined;
}

function parseDataStyle(dataStyle: string | null | undefined): Partial<ThemeTokens> {
  if (!dataStyle) return {};
  try {
    return JSON.parse(dataStyle) as Partial<ThemeTokens>;
  } catch {
    return {};
  }
}

function generateTokensCSS(tokens: ThemeTokens, selector: ':host' | ':root' = ':host'): string {
  const lines = Object.entries(tokens).map(
    ([key, value]) => `  --sd-${key}: ${value};`
  );
  return `${selector} {\n${lines.join('\n')}\n}`;
}

function injectThemeTokens(shadowRoot: ShadowRoot, tokens: ThemeTokens): void {
  const styleId = 'sd-theme-tokens';
  let styleEl = shadowRoot.querySelector(`#${styleId}`) as HTMLStyleElement | null;

  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = styleId;
    shadowRoot.appendChild(styleEl);
  }

  styleEl.textContent = generateTokensCSS(tokens);
}

function injectRootThemeTokens(tokens: ThemeTokens): (() => void) | undefined {
  if (typeof document === 'undefined') return undefined;
  const styleId = 'sd-theme-tokens';
  let styleEl = document.head.querySelector(`#${styleId}`) as HTMLStyleElement | null;

  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = styleId;
    document.head.appendChild(styleEl);
  }

  styleEl.textContent = generateTokensCSS(tokens, ':root');

  return () => {
    styleEl?.remove();
  };
}

export function ThemeProvider({ children, dataTheme, dataStyle, shadowRoot }: ThemeProviderProps) {
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    const stored = readStoredTheme();
    if (stored) return stored;
    return dataTheme === 'light' ? 'light' : 'dark';
  });

  const toggleTheme = useCallback(() => {
    setTheme((prev) => {
      const next = prev === 'light' ? 'dark' : 'light';
      try { localStorage.setItem(STORAGE_KEY, next); } catch { /* noop */ }
      return next;
    });
  }, []);

  const tokens = useMemo(() => {
    const defaultTokens = getDefaultTokens(theme);
    const overrides = parseDataStyle(dataStyle);
    return { ...defaultTokens, ...overrides };
  }, [theme, dataStyle]);

  useEffect(() => {
    if (shadowRoot) {
      injectThemeTokens(shadowRoot, tokens);
      return;
    }
    return injectRootThemeTokens(tokens);
  }, [shadowRoot, tokens]);

  return (
    <ThemeContext.Provider value={{ theme, tokens, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}
